// i18n entry for the webview package.
// Uses HTTP backend so that translation JSONs are loaded on demand from
// `public/locales/{lng}/{ns}.json`. This keeps the initial bundle small and
// allows updating translations without a full app rebuild.
import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export type SupportedLocale = 'zh-CN' | 'en-US' | 'bn-BD';

export const SUPPORTED_LOCALES: SupportedLocale[] = ['zh-CN', 'en-US', 'bn-BD'];
export const DEFAULT_LOCALE: SupportedLocale = 'bn-BD';
export const DOWNLOAD_DEFAULT_LOCALE: SupportedLocale = 'bn-BD';

// Display labels for the locale switcher dropdown / sheet.
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  'zh-CN': '中文',
  'en-US': 'English',
  'bn-BD': 'বাংলা',
};

// Persist the user's choice across sessions using a stable key.
export const LOCALE_STORAGE_KEY = 'cmp:locale';

const isDownloadRoute = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return /^\/download(?:\/detail)?\/?$/.test(window.location.pathname);
};

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: isDownloadRoute() ? DOWNLOAD_DEFAULT_LOCALE : DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    // Namespaces are loaded lazily; only `common` is loaded at boot to avoid
    // blocking the initial render. Pages declare their own namespace via
    // `useTranslation('xxx')` which triggers an HTTP fetch on first use.
    ns: ['common'],
    defaultNS: 'common',
    detection: {
      // First visits default to Bengali. A persisted user choice always wins.
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: LOCALE_STORAGE_KEY,
    },
    backend: {
      // Vite serves files from `public/` directly at site root. The dev server
      // and production build both expose `/locales/...` automatically.
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    interpolation: {
      // React already escapes by default, no need for i18next to do it.
      escapeValue: false,
    },
    react: {
      // Use Suspense so that the first render waits until the active locale
      // bundle is loaded; pair with a top-level <Suspense fallback={...}/>.
      useSuspense: true,
    },
  });

// Sync the <html lang> attribute on every language change so that browser
// hyphenation, screen readers and CSS `:lang(...)` selectors work correctly.
const syncHtmlLang = (lng: string): void => {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', lng);
  }
};

syncHtmlLang(i18n.language || DEFAULT_LOCALE);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
